/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : AppModal
 *   Feature      : 197-portal-light-theme-responsive
 *   Description  : The shared modal primitive every portal modal converges on.
 *                  Pure token-driven Tailwind (Spec 197 — MUI retirement) built on
 *                  the dependency-free useDraggable hook. Honors the modal contract:
 *                   - movable (drag the title bar)
 *                   - mode-aware scrim (light/dark) that keeps the page visible but
 *                     still BLOCKS input until dismissed
 *                   - distinct frame + shadow
 *                   - outside-click does NOT dismiss (unless dismissOnBackdrop)
 *                   - clamp: snapped back into view if left off-screen on resize
 *                   - Escape closes; token-styled surface
 *   Contract     : specs/197-portal-light-theme-responsive/contracts/modal.contract.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 *   Created      : 2026-06-13
 *   Modified     : 2026-06-15 | Peter Dobler | Re-based off MUI Dialog onto Tailwind.
 * ============================================================================
 */
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';
import { useDraggable } from './useDraggable';

export type AppModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'auto';

const SIZE_MAP: Record<AppModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  auto: 'max-w-fit',
};

export interface AppModalProps {
  /** Header content — also the drag handle. */
  title: React.ReactNode;
  open: boolean;
  onClose: () => void;
  size?: AppModalSize;
  /** Action row (buttons). */
  footer?: React.ReactNode;
  /** Click-outside closes (default **false** — outside click does NOT dismiss). */
  dismissOnBackdrop?: boolean;
  children?: React.ReactNode;
}

/**
 * Shared, movable, see-through-but-blocking modal with a frame + shadow.
 * Use everywhere a dialog is needed; do not hand-roll modals.
 */
export function AppModal({
  title,
  open,
  onClose,
  size = 'md',
  footer,
  dismissOnBackdrop = false,
  children,
}: AppModalProps) {
  const { transform, onHandleMouseDown, reset } = useDraggable();
  const paperRef = useRef<HTMLDivElement>(null);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // FR-009 — keep the modal reachable: if a dragged modal is left (almost) off
  // the viewport when the window resizes, snap it back to centre.
  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const onResize = () => {
      const r = paperRef.current?.getBoundingClientRect();
      if (!r) return;
      const offscreen =
        r.right < 48 ||
        r.left > window.innerWidth - 48 ||
        r.bottom < 48 ||
        r.top > window.innerHeight - 48;
      if (offscreen) reset();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open, reset]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="app-modal-title">
      {/* Mode-aware scrim: page stays visible, input is blocked. No outside-close unless opted in. */}
      <div
        className="fixed inset-0 bg-black/15 dark:bg-black/45"
        aria-hidden="true"
        onClick={dismissOnBackdrop ? onClose : undefined}
      />
      <div
        ref={paperRef}
        style={{ transform }}
        className={clsx(
          'relative flex max-h-[90vh] w-full flex-col overflow-hidden',
          'rounded-[var(--radius-lg)] border-2 border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-[var(--shadow-lg)]',
          SIZE_MAP[size],
        )}
      >
        <div
          onMouseDown={onHandleMouseDown}
          id="app-modal-title"
          className="flex cursor-move items-center justify-between border-b border-[var(--color-border)] px-5 py-3"
        >
          <div className="min-w-0 text-[length:var(--text-h3)] font-semibold text-[var(--color-text-primary)]">{title}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="ml-3 flex-shrink-0 text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3">{footer}</div>
        ) : null}
      </div>
    </div>
  );
}

export default AppModal;
